import React, { useCallback, useContext, useRef } from 'react'

import { Card, styled } from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { ErrorBoundary } from 'components/ErrorBoundary'
import config from 'config'
import { CurrentUserContext } from 'context/currentUserContext'
import useDwellTime from 'hooks/useDwellTime'
import { Insight } from 'modules/accountActivity/domain'

import EngagementScoreCard from './EngagementScoreCard'
import MeetingRecordingCard from './MeetingRecordingCard'
import WebActivityCard from './WebActivityCard'

interface CCRMFeedCardProps {
  insight: Insight
  countCardDwellTime: (insightId: number, timeInMs: number) => void
  commentsInitiallyOpen?: boolean
}

export const InsightType = {
  ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS: 'ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS',
  WEB_ACTIVITY: 'WEB_ACTIVITY',
  ACCOUNT_SUMMARY: 'ACCOUNT_SUMMARY',
  MEETING_RECORDING: 'MEETING_RECORDING'
} as const

export type InsightTypeListType = (
  | 'ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS'
  | 'WEB_ACTIVITY'
  | 'MEETING_RECORDING'
  | 'ACCOUNT_SUMMARY'
  | undefined
)[]

// If we start having more insight types, we should add the type to this array
export const insightTypeList: InsightTypeListType = [
  InsightType.ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS,
  InsightType.WEB_ACTIVITY,
  InsightType.MEETING_RECORDING,
  InsightType.ACCOUNT_SUMMARY
]

const cardFactory = (insight: Insight, hasMeetingRecordingCard: boolean) => {
  switch (insight.insightType) {
    case InsightType.ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS:
    case InsightType.WEB_ACTIVITY:
      return <WebActivityCard insight={insight} />
    case InsightType.ACCOUNT_SUMMARY:
      return <EngagementScoreCard insight={insight} />
    case InsightType.MEETING_RECORDING:
      return hasMeetingRecordingCard ? <MeetingRecordingCard insight={insight} /> : null
    default:
      return null
  }
}

const FeedCard = styled(Card, {
  margin: '16px 0px',
  minWidth: '600px'
})

const CCRMFeedCard: React.FC<CCRMFeedCardProps> = ({
  insight,
  countCardDwellTime,
}) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const trackDwellTime = useCallback(
    (totalDwellTime: number) => countCardDwellTime(insight.id, totalDwellTime),
    [countCardDwellTime, insight.id]
  )

  useDwellTime({ elementRef, onDwellStart: trackDwellTime, onDwellEnd: trackDwellTime })

  const currentUser = useContext(CurrentUserContext)
  const hasMeetingRecordingCard =
    access.hasGates('meeting_recording_card') ||
    config.MEETING_RECORDING_CARD_WHITELIST?.includes(currentUser?.id)

  const cardBody = cardFactory(insight, hasMeetingRecordingCard)
  return (
    cardBody && (
      <ErrorBoundary errorState={null}>
        <FeedCard ref={elementRef}>
          {cardBody}
        </FeedCard>
      </ErrorBoundary>
    )
  )
}

export default CCRMFeedCard
